import { useState, useRef } from "react";
import { useSelector } from "react-redux";
import { createPlaylist, updatePlaylist } from "../../functions/playlist";
import { uploadImages } from "../../functions/uploadImages";
import "./style.css";

export default function CreatePlaylistModal({
  visible,
  onClose,
  onPlaylistCreated,
  editPlaylist = null,
}) {
  const { user } = useSelector((state) => ({ ...state }));
  const [title, setTitle] = useState(editPlaylist?.title || "");
  const [description, setDescription] = useState(editPlaylist?.description || "");
  const [privacy, setPrivacy] = useState(editPlaylist?.privacy || "public");
  const [thumbnail, setThumbnail] = useState(editPlaylist?.thumbnail || null);
  const [thumbnailFile, setThumbnailFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const fileInputRef = useRef(null);

  const handleThumbnailChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith("image/")) {
        setError("Por favor, selecione uma imagem válida");
        return;
      }
      setThumbnailFile(file);
      const reader = new FileReader();
      reader.onload = (e) => {
        setThumbnail(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeThumbnail = () => {
    setThumbnail(null);
    setThumbnailFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleSubmit = async () => {
    if (!title.trim()) {
      setError("Título é obrigatório");
      return;
    }

    setLoading(true);
    setError("");

    try {
      let thumbnailUrl = thumbnail;

      // Upload thumbnail if new file
      if (thumbnailFile) {
        const formData = new FormData();
        formData.append("file", thumbnailFile);
        formData.append("path", `${user.id}/playlist_thumbnails`);
        const uploadRes = await uploadImages(formData, user.token);
        if (uploadRes.status === "ok" && uploadRes.images?.length > 0) {
          thumbnailUrl = uploadRes.images[0].url;
        }
      }

      const playlistData = {
        title: title.trim(),
        description: description.trim(),
        privacy,
        thumbnail: thumbnailUrl,
      };

      let res;
      if (editPlaylist) {
        res = await updatePlaylist(editPlaylist._id, playlistData, user.token);
      } else {
        res = await createPlaylist(playlistData, user.token);
      }

      if (res.status === "ok") {
        onPlaylistCreated(res.data);
        handleClose();
      } else {
        setError(res.message || "Erro ao salvar playlist");
      }
    } catch (err) {
      setError("Erro ao salvar playlist");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setTitle("");
    setDescription("");
    setPrivacy("public");
    setThumbnail(null);
    setThumbnailFile(null);
    setError("");
    onClose();
  };

  if (!visible) return null;

  return (
    <div className="create_playlist_modal_overlay" onClick={handleClose}>
      <div
        className="create_playlist_modal"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="create_playlist_modal_header">
          <h2>{editPlaylist ? "Editar Playlist" : "Criar Playlist"}</h2>
          <div className="create_playlist_modal_close" onClick={handleClose}>
            <svg viewBox="0 0 24 24">
              <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
            </svg>
          </div>
        </div>

        <div className="create_playlist_modal_body">
          {error && (
            <div
              style={{
                padding: "10px",
                background: "var(--red-light)",
                color: "var(--red-color)",
                borderRadius: "8px",
                marginBottom: "16px",
                fontSize: "14px",
              }}
            >
              {error}
            </div>
          )}

          <div className="create_playlist_form_group">
            <label>Capa (opcional)</label>
            <input
              type="file"
              ref={fileInputRef}
              accept="image/*"
              style={{ display: "none" }}
              onChange={handleThumbnailChange}
            />
            <div
              className="playlist_thumbnail_upload"
              onClick={() => fileInputRef.current?.click()}
            >
              {thumbnail ? (
                <>
                  <img src={thumbnail} alt="Thumbnail" />
                  <div
                    className="remove_thumbnail"
                    onClick={(e) => {
                      e.stopPropagation();
                      removeThumbnail();
                    }}
                  >
                    <svg viewBox="0 0 24 24">
                      <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
                    </svg>
                  </div>
                </>
              ) : (
                <>
                  <svg viewBox="0 0 24 24">
                    <path d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z" />
                  </svg>
                  <span>Clique para adicionar capa</span>
                </>
              )}
            </div>
          </div>

          <div className="create_playlist_form_group">
            <label>Título *</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Nome da playlist"
              maxLength={100}
            />
          </div>

          <div className="create_playlist_form_group">
            <label>Descrição</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Adicione uma descrição..."
              maxLength={500}
            />
          </div>

          <div className="create_playlist_form_group">
            <label>Privacidade</label>
            <div className="privacy_options">
              <div
                className={`privacy_option ${privacy === "public" ? "selected" : ""}`}
                onClick={() => setPrivacy("public")}
              >
                <svg viewBox="0 0 24 24">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z" />
                </svg>
                <span>Público</span>
              </div>
              <div
                className={`privacy_option ${privacy === "friends" ? "selected" : ""}`}
                onClick={() => setPrivacy("friends")}
              >
                <svg viewBox="0 0 24 24">
                  <path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z" />
                </svg>
                <span>Amigos</span>
              </div>
              <div
                className={`privacy_option ${privacy === "only_me" ? "selected" : ""}`}
                onClick={() => setPrivacy("only_me")}
              >
                <svg viewBox="0 0 24 24">
                  <path d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zm-6 9c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zm3.1-9H8.9V6c0-1.71 1.39-3.1 3.1-3.1 1.71 0 3.1 1.39 3.1 3.1v2z" />
                </svg>
                <span>Privado</span>
              </div>
            </div>
          </div>
        </div>

        <div className="create_playlist_modal_footer">
          <button
            className="create_playlist_cancel_btn"
            onClick={handleClose}
            disabled={loading}
          >
            Cancelar
          </button>
          <button
            className="create_playlist_submit_btn"
            onClick={handleSubmit}
            disabled={loading || !title.trim()}
          >
            {loading ? "Salvando..." : editPlaylist ? "Salvar" : "Criar"}
          </button>
        </div>
      </div>
    </div>
  );
}
